/**
 * /producao/config — Configurações da Produção (Sprint P5).
 *
 * Rail vertical de domínios (esquerda, sticky) + zona principal com os
 * field-rows do domínio ativo (brief §1.4). Domínios: os 6 do catálogo
 * declarativo do backend (GET /api/producao/config/catalogo) + Postos de
 * Bipagem + Mão de obra & Competências + seção Roadmap com cards honestos
 * (disciplina D1 — o que NÃO é configurável agora diz o que o sistema usa
 * hoje, sem prometer).
 *
 * Padrões:
 *   - Salvar POR SEÇÃO (savebar sticky aparece só com mudança não-salva;
 *     trocar de domínio com seção suja pede confirmação inline).
 *   - Busca de parâmetro client-side (label/chave/descrição) → navega ao
 *     domínio + highlight pulsante no field-row (~1,2s).
 *   - origem default/personalizado por chave; "Restaurar padrão" = PUT com
 *     o default (o backend não tem DELETE de parâmetro).
 *   - Sem ppcp:configurar → tela read-only (controles disabled, savebar
 *     oculta, banner info).
 *   - Navegação persistida em localStorage `prod.config.dominio` + query
 *     `?dominio=`.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FiActivity,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiCloudSnow,
  FiCpu,
  FiLock,
  FiTarget,
  FiUsers,
} from "react-icons/fi";
import { useSearchParams } from "react-router-dom";

import AsyncButton from "../../components/AsyncButton";
import EmptyState from "../../components/EmptyState";
import EntityHeader from "../../components/EntityHeader";
import { useAuth } from "../../context/AuthContext";
import {
  coberturaPostos,
  configCatalogo,
  salvarParametrosConfig,
} from "../../services/producaoV2";
import { unidadeDefault } from "../../services/unidades";
import { normalizeText } from "../../utils/data";
import { PageContainer } from "../../styles/shared";
import {
  EmptyMsg,
  ErrorMsg,
  InfoMsg,
  Pill,
  SearchInput,
  SecondaryButton,
  WarningMsg,
} from "../Orcamentos/styles";
import { LinhaErro, Mono, NotaDiscreta, Shimmer } from "./_shared/ui";
import { invalidarConfigProducao } from "./_shared/useConfigProducao";
import DominioParametros from "./_config/DominioParametros";
import MaoDeObraDominio from "./_config/MaoDeObraDominio";
import PostosDominio from "./_config/PostosDominio";
import {
  CardRoadmap,
  LayoutConfig,
  Rail,
  RailItem,
  RailTitulo,
  SaveBar,
  ZonaPrincipal,
} from "./_config/ui";

const CHAVE_DOMINIO = "prod.config.dominio";
const DOMINIO_POSTOS = "postos";
const DOMINIO_MAO_DE_OBRA = "mao_de_obra";
const HIGHLIGHT_MS = 1500;

const ICONE_DOMINIO = {
  calendario: FiCalendar,
  toc: FiTarget,
  motor_empirico: FiActivity,
  congelamento: FiCloudSnow,
  qualidade: FiCheckCircle,
  prazos: FiClock,
  [DOMINIO_POSTOS]: FiCpu,
  [DOMINIO_MAO_DE_OBRA]: FiUsers,
};

/** Cards Roadmap honestos (D1): não esconder, não prometer. */
const ROADMAP_ITEMS = [
  {
    id: "roadmap_roteiro",
    label: "Roteiro configurável",
    hoje: "o roteiro usa o catálogo fixo de 23 etapas em 6 fases (padrão "
      + "do sistema). Criar/renomear fases e etapas, gates tipados e "
      + "condicionais por característica do BOM chegam com o editor de "
      + "roteiro.",
  },
  {
    id: "roadmap_estacoes",
    label: "Estações & Maquinário (OEE)",
    hoje: "a capacidade deriva do calendário × eficiência planejada — não "
      + "há cadastro de estações/máquinas nem OEE (digitado ou medido).",
  },
  {
    id: "roadmap_notificacoes",
    label: "Notificações",
    hoje: "os alertas aparecem nas próprias telas (banners, badges e KPIs); "
      + "não há envio configurável por evento/canal/destinatário.",
  },
  {
    id: "roadmap_embalagem",
    label: "Embalagem & Expedição",
    hoje: "o fim de F6 é o marco Carregado — tipos de embalagem, checklist "
      + "de pré-carregamento e janelas de doca não são configuráveis.",
  },
  {
    id: "roadmap_lote",
    label: "Política de Lote",
    hoje: "o agrupamento de lote é manual na tela Otimização & Lotes (sem "
      + "critérios automáticos de elegibilidade nem tamanho-alvo).",
  },
  {
    id: "roadmap_multifabrica",
    label: "Multi-fábrica",
    hoje: "a configuração vale para a fábrica única cadastrada; perfis por "
      + "fábrica (herdar × personalizar) chegam com a segunda planta.",
  },
];

const igual = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

function lerDominioInicial(searchParams) {
  const daUrl = searchParams.get("dominio");
  if (daUrl) return daUrl;
  try {
    return window.localStorage.getItem(CHAVE_DOMINIO) || "calendario";
  } catch {
    return "calendario";
  }
}

export default function Config() {
  const { request, hasPermission } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const podeVer = hasPermission("ppcp:view");
  const podeConfigurar = hasPermission("ppcp:configurar");
  // Postos e Mão de obra têm permissão própria: quem administra PIN
  // (operador:view) ou postos (posto_bipagem:view) chega aqui pelo menu
  // sem ter ppcp:view e precisa ver ao menos a sua seção.
  const podeVerPostos = podeVer || hasPermission("posto_bipagem:view");
  const podeVerMaoDeObra = podeVer || hasPermission("operador:view");
  const podeVerAlgo = podeVer || podeVerPostos || podeVerMaoDeObra;

  const [catalogo, setCatalogo] = useState(null); // {dominios, parametros}
  const [unidade, setUnidade] = useState(null);
  const [cobertura, setCobertura] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);

  const [dominioEscolhido, setDominioAtivo] = useState(() =>
    lerDominioInicial(searchParams),
  );
  // Sem ppcp:view só as seções de cadastro liberadas existem; um domínio
  // lembrado do localStorage ou vindo da URL cai na primeira permitida.
  const dominioAtivo = (() => {
    if (podeVer) return dominioEscolhido;
    if (dominioEscolhido === DOMINIO_POSTOS && podeVerPostos) return DOMINIO_POSTOS;
    if (dominioEscolhido === DOMINIO_MAO_DE_OBRA && podeVerMaoDeObra) {
      return DOMINIO_MAO_DE_OBRA;
    }
    return podeVerPostos ? DOMINIO_POSTOS : DOMINIO_MAO_DE_OBRA;
  })();
  const [edicoes, setEdicoes] = useState({});
  const [errosPorChave, setErrosPorChave] = useState({});
  const [erroSalvar, setErroSalvar] = useState(null);
  const [trocaPendente, setTrocaPendente] = useState(null);

  const [busca, setBusca] = useState("");
  const [buscaAberta, setBuscaAberta] = useState(false);
  const buscaRef = useRef(null);
  const [highlightChave, setHighlightChave] = useState(null);
  const highlightTimer = useRef(null);

  // ---- cargas ----
  const carregarCatalogo = useCallback(async () => {
    if (!podeVer) {
      setCarregando(false);
      return;
    }
    setErro(null);
    setCarregando(true);
    try {
      setCatalogo(await configCatalogo(request));
    } catch (err) {
      setErro(err?.message || "Não foi possível carregar as configurações.");
    } finally {
      setCarregando(false);
    }
  }, [request, podeVer]);

  const carregarCobertura = useCallback(() => {
    if (!podeVer) return;
    coberturaPostos(request)
      .then(setCobertura)
      .catch(() => setCobertura(null));
  }, [request, podeVer]);

  useEffect(() => {
    carregarCatalogo();
    carregarCobertura();
  }, [carregarCatalogo, carregarCobertura]);

  useEffect(() => {
    unidadeDefault(request).then(setUnidade);
  }, [request]);

  // ---- navegação (localStorage + ?dominio=) ----
  useEffect(() => {
    try {
      window.localStorage.setItem(CHAVE_DOMINIO, dominioAtivo);
    } catch { /* storage indisponível */ }
    const atual = searchParams.get("dominio");
    if (atual !== dominioAtivo) {
      setSearchParams({ dominio: dominioAtivo }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dominioAtivo]);

  useEffect(() => () => {
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
  }, []);

  // Fecha o popover da busca ao clicar fora / ESC.
  useEffect(() => {
    if (!buscaAberta) return undefined;
    const onClickFora = (e) => {
      if (buscaRef.current && !buscaRef.current.contains(e.target)) {
        setBuscaAberta(false);
      }
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setBuscaAberta(false);
    };
    document.addEventListener("mousedown", onClickFora);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClickFora);
      document.removeEventListener("keydown", onEsc);
    };
  }, [buscaAberta]);

  // ---- catálogo indexado ----
  const parametros = useMemo(() => catalogo?.parametros || [], [catalogo]);
  const porChave = useMemo(
    () => Object.fromEntries(parametros.map((p) => [p.chave, p])),
    [parametros],
  );
  const dominiosCatalogo = useMemo(() => catalogo?.dominios || [], [catalogo]);
  const rotuloDominio = useMemo(() => {
    const mapa = Object.fromEntries(dominiosCatalogo.map((d) => [d.id, d.label]));
    mapa[DOMINIO_POSTOS] = "Postos de Bipagem";
    mapa[DOMINIO_MAO_DE_OBRA] = "Mão de obra & Competências";
    for (const item of ROADMAP_ITEMS) mapa[item.id] = item.label;
    return mapa;
  }, [dominiosCatalogo]);

  const entradasDoDominio = useMemo(
    () => parametros.filter((p) => p.dominio === dominioAtivo),
    [parametros, dominioAtivo],
  );

  // ---- edição / dirty ----
  const chavesSujas = useMemo(
    () =>
      Object.keys(edicoes).filter(
        (chave) => porChave[chave] && !igual(edicoes[chave], porChave[chave].valor),
      ),
    [edicoes, porChave],
  );
  const temSujeira = chavesSujas.length > 0;

  const valoresEfetivos = useMemo(() => {
    const mapa = {};
    for (const p of entradasDoDominio) {
      mapa[p.chave] = p.chave in edicoes ? edicoes[p.chave] : p.valor;
    }
    return mapa;
  }, [entradasDoDominio, edicoes]);

  const editar = useCallback((chave, valor) => {
    setEdicoes((atual) => ({ ...atual, [chave]: valor }));
    setErrosPorChave((atual) => {
      if (!atual[chave]) return atual;
      const novo = { ...atual };
      delete novo[chave];
      return novo;
    });
  }, []);

  const restaurarPadrao = useCallback((entrada) => {
    // Restaurar = PUT com o default (não há DELETE de parâmetro no backend).
    setEdicoes((atual) => ({ ...atual, [entrada.chave]: entrada.default }));
  }, []);

  const descartar = useCallback(() => {
    setEdicoes({});
    setErrosPorChave({});
    setErroSalvar(null);
  }, []);

  const salvarSecao = useCallback(async () => {
    setErroSalvar(null);
    setErrosPorChave({});
    const payload = {};
    for (const chave of chavesSujas) payload[chave] = edicoes[chave] ?? null;
    try {
      const resposta = await salvarParametrosConfig(request, payload);
      // Consumidores do catálogo (checkpoints, política de pausa do
      // congelamento…) releem na próxima montagem.
      invalidarConfigProducao();
      if (resposta?.parametros) {
        setCatalogo((atual) => ({ ...atual, parametros: resposta.parametros }));
      } else {
        await carregarCatalogo();
      }
      setEdicoes({});
    } catch (err) {
      setErrosPorChave(err?.payload?.erros || {});
      setErroSalvar(
        err?.payload?.detalhe || err?.message || "Não foi possível salvar.",
      );
      throw err;
    }
  }, [request, chavesSujas, edicoes, carregarCatalogo]);

  const destacarChave = useCallback((chave) => {
    setHighlightChave(chave);
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(
      () => setHighlightChave(null),
      HIGHLIGHT_MS,
    );
  }, []);

  // ---- troca de domínio com guarda de sujeira ----
  const irParaDominio = useCallback(
    (id, chave) => {
      if (temSujeira && id !== dominioAtivo) {
        setTrocaPendente({ alvo: id, chave: chave || null });
        return;
      }
      setDominioAtivo(id);
      if (chave) destacarChave(chave);
    },
    [temSujeira, dominioAtivo, destacarChave],
  );

  // ---- busca (client-side sobre o catálogo) ----
  const resultadosBusca = useMemo(() => {
    const needle = normalizeText(busca.trim());
    if (!needle) return [];
    return parametros
      .filter((p) =>
        normalizeText(`${p.label} ${p.chave} ${p.descricao}`).includes(needle),
      )
      .slice(0, 8);
  }, [busca, parametros]);

  const valorCurto = (p) => {
    if (p.valor === null || p.valor === undefined) return "–";
    if (typeof p.valor === "object") return "(estruturado)";
    return String(p.valor);
  };

  // ---- render ----
  if (!podeVerAlgo) {
    return (
      <PageContainer>
        <EntityHeader
          title="Configurações da Produção"
          subtitle="Parametriza o que a fábrica precisa para rodar"
        />
        <EmptyState
          tone="locked"
          titulo="Sem acesso às configurações"
          descricao="Esta tela exige acesso de leitura ao PPCP. Fale com o gestor de acessos."
        />
      </PageContainer>
    );
  }

  const badgePostos =
    cobertura && (cobertura.descobertas || []).length > 0
      ? {
          tone: cobertura.tambor_descoberto ? "danger" : "warning",
          label: String(cobertura.descobertas.length),
        }
      : null;

  const roadmapAtivo = ROADMAP_ITEMS.find((r) => r.id === dominioAtivo) || null;

  return (
    <PageContainer>
      <EntityHeader
        title="Configurações da Produção"
        subtitle="Parametriza o que a fábrica precisa para rodar"
        badges={
          unidade
            ? [
                {
                  key: "unidade",
                  label: `${unidade.codigo} · ${unidade.nome}`,
                  tone: "info",
                },
              ]
            : []
        }
      />

      {podeVer && !podeConfigurar && (
        <InfoMsg>
          Você tem acesso de leitura às configurações — alterar exige a
          permissão de configurar o PPCP.
        </InfoMsg>
      )}

      {podeVer && (
      <div ref={buscaRef} style={{ position: "relative", maxWidth: 460 }}>
        <SearchInput
          type="search"
          value={busca}
          placeholder="Buscar parâmetro… (ex.: buffer, feriado, prior)"
          aria-label="Buscar parâmetro"
          onChange={(e) => {
            setBusca(e.target.value);
            setBuscaAberta(true);
          }}
          onFocus={() => busca && setBuscaAberta(true)}
          style={{ width: "100%" }}
        />
        {buscaAberta && busca.trim() && (
          <div
            role="listbox"
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              right: 0,
              zIndex: 60,
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-md, 8px)",
              boxShadow: "var(--shadow-2)",
              padding: 4,
              maxHeight: 320,
              overflowY: "auto",
            }}
          >
            {resultadosBusca.length === 0 && (
              <div style={{ padding: "10px 12px", fontSize: 13, color: "var(--ink-3)" }}>
                Nenhum parâmetro encontrado para «{busca.trim()}».
              </div>
            )}
            {resultadosBusca.map((p) => (
              <button
                key={p.chave}
                type="button"
                role="option"
                aria-selected="false"
                onClick={() => {
                  setBuscaAberta(false);
                  setBusca("");
                  irParaDominio(p.dominio, p.chave);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: "100%",
                  padding: "8px 10px",
                  border: 0,
                  background: "transparent",
                  cursor: "pointer",
                  textAlign: "left",
                  fontSize: 13,
                  color: "var(--ink)",
                  borderRadius: 6,
                }}
              >
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {p.label}
                </span>
                <Pill $tone="neutral">{rotuloDominio[p.dominio] || p.dominio}</Pill>
                <Mono style={{ fontSize: 11, color: "var(--ink-3)" }}>
                  {valorCurto(p)}
                </Mono>
              </button>
            ))}
          </div>
        )}
      </div>
      )}

      {erro && (
        <LinhaErro>
          <ErrorMsg style={{ flex: 1 }}>{erro}</ErrorMsg>
          <SecondaryButton type="button" onClick={carregarCatalogo}>
            Tentar de novo
          </SecondaryButton>
        </LinhaErro>
      )}

      {carregando ? (
        <LayoutConfig>
          <Shimmer $h={360} />
          <Shimmer $h={480} />
        </LayoutConfig>
      ) : (catalogo || !podeVer) && (
        <LayoutConfig>
          <Rail aria-label="Domínios de configuração">
            {podeVer && <RailTitulo>Parâmetros</RailTitulo>}
            {dominiosCatalogo.map((d) => {
              const Icone = ICONE_DOMINIO[d.id] || FiTarget;
              return (
                <RailItem
                  key={d.id}
                  type="button"
                  $ativo={dominioAtivo === d.id}
                  onClick={() => irParaDominio(d.id)}
                >
                  <Icone size={14} aria-hidden="true" />
                  <span className="rotulo">{d.label}</span>
                </RailItem>
              );
            })}
            <RailTitulo>Cadastros</RailTitulo>
            {podeVerPostos && (
              <RailItem
                type="button"
                $ativo={dominioAtivo === DOMINIO_POSTOS}
                onClick={() => irParaDominio(DOMINIO_POSTOS)}
              >
                <FiCpu size={14} aria-hidden="true" />
                <span className="rotulo">Postos de Bipagem</span>
                {badgePostos && (
                  <Pill $tone={badgePostos.tone}>{badgePostos.label}</Pill>
                )}
              </RailItem>
            )}
            {podeVerMaoDeObra && (
              <RailItem
                type="button"
                $ativo={dominioAtivo === DOMINIO_MAO_DE_OBRA}
                onClick={() => irParaDominio(DOMINIO_MAO_DE_OBRA)}
              >
                <FiUsers size={14} aria-hidden="true" />
                <span className="rotulo">Mão de obra & Competências</span>
              </RailItem>
            )}
            {podeVer && <RailTitulo>Roadmap</RailTitulo>}
            {podeVer && ROADMAP_ITEMS.map((item) => (
              <RailItem
                key={item.id}
                type="button"
                $ativo={dominioAtivo === item.id}
                $roadmap
                onClick={() => irParaDominio(item.id)}
                title="Evolução — não configurável agora"
              >
                <FiLock size={13} aria-hidden="true" />
                <span className="rotulo">{item.label}</span>
              </RailItem>
            ))}
          </Rail>

          <ZonaPrincipal>
            {trocaPendente && (
              <WarningMsg>
                Você tem {chavesSujas.length} alteração(ões) não salva(s) em{" "}
                {rotuloDominio[dominioAtivo] || dominioAtivo}. Salvar antes de
                sair?
                <span
                  style={{
                    display: "inline-flex",
                    gap: 8,
                    marginLeft: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <AsyncButton
                    variant="secondary"
                    silentError
                    successMessage="Seção salva."
                    onClick={async () => {
                      await salvarSecao();
                      const alvo = trocaPendente;
                      setTrocaPendente(null);
                      setDominioAtivo(alvo.alvo);
                      if (alvo.chave) destacarChave(alvo.chave);
                    }}
                  >
                    Salvar
                  </AsyncButton>
                  <SecondaryButton
                    type="button"
                    onClick={() => {
                      descartar();
                      const alvo = trocaPendente;
                      setTrocaPendente(null);
                      setDominioAtivo(alvo.alvo);
                      if (alvo.chave) destacarChave(alvo.chave);
                    }}
                  >
                    Descartar
                  </SecondaryButton>
                  <SecondaryButton
                    type="button"
                    onClick={() => setTrocaPendente(null)}
                  >
                    Cancelar
                  </SecondaryButton>
                </span>
              </WarningMsg>
            )}

            {roadmapAtivo ? (
              <CardRoadmap>
                <h3>
                  <FiLock size={15} aria-hidden="true" />
                  {roadmapAtivo.label}
                  <Pill $tone="neutral">Roadmap</Pill>
                </h3>
                Evolução — não configurável agora. Hoje o sistema usa:{" "}
                {roadmapAtivo.hoje}
              </CardRoadmap>
            ) : dominioAtivo === DOMINIO_POSTOS ? (
              <PostosDominio
                cobertura={cobertura}
                onCoberturaMudou={carregarCobertura}
              />
            ) : dominioAtivo === DOMINIO_MAO_DE_OBRA ? (
              <MaoDeObraDominio />
            ) : entradasDoDominio.length === 0 ? (
              <EmptyMsg>
                Nenhum parâmetro neste domínio — selecione outro no rail.
              </EmptyMsg>
            ) : (
              <>
                <DominioParametros
                  dominio={dominioAtivo}
                  entradas={entradasDoDominio}
                  valores={valoresEfetivos}
                  editadas={new Set(chavesSujas)}
                  errosPorChave={errosPorChave}
                  disabled={!podeConfigurar}
                  highlightChave={highlightChave}
                  onChange={editar}
                  onRestaurar={restaurarPadrao}
                />
                <NotaDiscreta>
                  As mesmas chaves alimentam o board da Marcenaria, o DBR e a
                  Análise de Atrasos — mudar aqui muda o cálculo lá.
                </NotaDiscreta>
              </>
            )}

            {podeConfigurar && temSujeira && !roadmapAtivo
              && dominioAtivo !== DOMINIO_POSTOS
              && dominioAtivo !== DOMINIO_MAO_DE_OBRA && (
              <SaveBar>
                <span className="contagem">
                  {chavesSujas.length} alteração
                  {chavesSujas.length === 1 ? "" : "ões"} nesta seção
                </span>
                {erroSalvar && (
                  <span style={{ color: "var(--danger-text)", fontSize: 12 }}>
                    {erroSalvar}
                  </span>
                )}
                <span className="espaco" />
                <SecondaryButton type="button" onClick={descartar}>
                  Descartar
                </SecondaryButton>
                <AsyncButton
                  silentError
                  successMessage="Seção salva."
                  onClick={salvarSecao}
                >
                  Salvar seção
                </AsyncButton>
              </SaveBar>
            )}
          </ZonaPrincipal>
        </LayoutConfig>
      )}
    </PageContainer>
  );
}
